import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GoogleMap, DirectionsService, DirectionsRenderer, MarkerF } from '@react-google-maps/api';
import { Spinner } from 'react-bootstrap';

const containerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '400px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: -33.4489, // Santiago
  lng: -70.6693
};

const isValidCoordinate = (loc) => {
  return loc && 
         loc.lat !== null && 
         loc.lat !== undefined && 
         loc.lat !== '' &&
         loc.lat !== 'null' &&
         !isNaN(Number(loc.lat)) && 
         loc.lng !== null && 
         loc.lng !== undefined && 
         loc.lng !== '' &&
         loc.lng !== 'null' &&
         !isNaN(Number(loc.lng));
};

const RouteMap = ({ pickupLocation, dropoffLocation, stops = [], onDistanceCalculated }) => {
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Guardamos la última solicitud procesada para evitar loops
  const lastRequestRef = useRef({ origin: null, destination: null, waypoints: null });

  // Referencia al mapa
  const mapRef = useRef(null);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Efecto para centrar y encuadrar el mapa cuando hay coordenadas
  useEffect(() => {
    if (mapRef.current && window.google) {
        const hasPickup = isValidCoordinate(pickupLocation);
        const hasDropoff = isValidCoordinate(dropoffLocation);

        if (hasPickup || hasDropoff) {
            const bounds = new window.google.maps.LatLngBounds();
            
            if (hasPickup) bounds.extend({ lat: Number(pickupLocation.lat), lng: Number(pickupLocation.lng) });
            if (hasDropoff) bounds.extend({ lat: Number(dropoffLocation.lat), lng: Number(dropoffLocation.lng) });
            
            // Añadir paradas (waypoints) si las hay
            stops.forEach(s => {
                if (isValidCoordinate(s)) bounds.extend({ lat: Number(s.lat), lng: Number(s.lng) });
            });

            if (hasPickup && hasDropoff) {
                mapRef.current.fitBounds(bounds, { padding: 50 });
            } else {
                // Si solo hay uno, centramos ahí
                const center = hasPickup 
                    ? { lat: Number(pickupLocation.lat), lng: Number(pickupLocation.lng) } 
                    : { lat: Number(dropoffLocation.lat), lng: Number(dropoffLocation.lng) };
                mapRef.current.panTo(center);
                mapRef.current.setZoom(15);
            }
        }
    }
  }, [pickupLocation, dropoffLocation, stops]);

  // Callback de DirectionsService
  const directionsCallback = useCallback((res) => {
    if (res !== null) {
      console.log(`🗺️ [RouteMap] Directions response: ${res.status}`);
      setLoading(false); // Siempre quitar loading al recibir respuesta
      
      if (res.status === 'OK') {
        setResponse(res);
        setError('');
        
        let totalDistance = 0;
        let totalDurationSec = 0;
        
        if (res.routes.length > 0) {
            const route = res.routes[0];
            route.legs.forEach(leg => {
                totalDistance += leg.distance.value;
                totalDurationSec += leg.duration.value;
            });
            
            const distanceKm = totalDistance / 1000;
            const durationMinutes = Math.ceil(totalDurationSec / 60);

            if (onDistanceCalculated) {
                onDistanceCalculated(distanceKm, durationMinutes);
            }
        }
      } else {
        console.error(`❌ [RouteMap] Error Directions: ${res.status}`);
        if (res.status === 'ZERO_RESULTS') {
            setError('No se encontró una ruta.');
        } else if (res.status !== 'REQUEST_DENIED') {
            setError(`Error de mapa: ${res.status}`);
        }
        setResponse(null);
      }
    }
  }, [onDistanceCalculated]); 

  // Calcular opciones de dirección SOLO si las coordenadas han cambiado
  const directionsOptions = useMemo(() => {
      const hasPickup = isValidCoordinate(pickupLocation);
      const hasDropoff = isValidCoordinate(dropoffLocation);

      if (!hasPickup || !hasDropoff) return null;

      const waypoints = (stops || [])
          .filter(s => isValidCoordinate(s))
          .slice(0, 23) // Google Maps API limita a máximo 23 waypoints por consulta
          .map(s => ({
              location: { lat: Number(s.lat), lng: Number(s.lng) },
              stopover: true
          }));

      const currentOrigin = `${pickupLocation.lat},${pickupLocation.lng}`;
      const currentDest = `${dropoffLocation.lat},${dropoffLocation.lng}`;
      const currentWaypoints = JSON.stringify(waypoints);

      if (
          lastRequestRef.current.origin === currentOrigin && 
          lastRequestRef.current.destination === currentDest &&
          lastRequestRef.current.waypoints === currentWaypoints &&
          response !== null 
      ) {
          return null;
      }

      return {
          googleOptions: {
            origin: { lat: Number(pickupLocation.lat), lng: Number(pickupLocation.lng) },
            destination: { lat: Number(dropoffLocation.lat), lng: Number(dropoffLocation.lng) },
            waypoints: waypoints,
            travelMode: 'DRIVING'
          },
          _meta: { origin: currentOrigin, destination: currentDest, waypoints: currentWaypoints }
      };
  }, [pickupLocation, dropoffLocation, stops, response]);

  // Efecto para controlar el estado de carga
  useEffect(() => {
      if (directionsOptions && directionsOptions._meta) {
          lastRequestRef.current = directionsOptions._meta;
          setLoading(true);
          setError('');
          setResponse(null);

          const timer = setTimeout(() => {
              setLoading(false);
          }, 4000);
          return () => clearTimeout(timer);
      }
  }, [directionsOptions]);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
            streetViewControl: false,
            mapTypeControl: false,
            gestureHandling: "cooperative"
        }}
      >
        {/* Usamos una key única para obligar a DirectionsService a re-activarse cuando la ruta cambia */}
        {directionsOptions && mapRef.current && (
          <DirectionsService
            key={`${directionsOptions._meta.origin}-${directionsOptions._meta.destination}-${stops.length}`}
            options={directionsOptions.googleOptions} 
            callback={directionsCallback}
          />
        )}

        {/* Renderizar Ruta si existe */}
        {response && (
          <DirectionsRenderer
            options={{
              directions: response,
              suppressMarkers: false // Dejar que Google ponga marcadores
            }}
          />
        )}

        {/* Marcadores manuales si NO hay ruta aun */}
        {!response && isValidCoordinate(pickupLocation) && (
            <MarkerF position={{ lat: Number(pickupLocation.lat), lng: Number(pickupLocation.lng) }} label="A" />
        )}
        {!response && isValidCoordinate(dropoffLocation) && (
            <MarkerF position={{ lat: Number(dropoffLocation.lat), lng: Number(dropoffLocation.lng) }} label="B" />
        )}
      </GoogleMap>
      
      {loading && (
          <div className="position-absolute top-50 start-50 translate-middle bg-white p-2 rounded shadow" style={{zIndex: 20}}>
              <Spinner animation="border" size="sm" variant="primary" className="me-2" />
              <small>Calculando ruta...</small>
          </div>
      )}

      {error && (
          <div className="position-absolute top-0 start-50 translate-middle-x mt-2 bg-danger text-white p-2 rounded shadow" style={{zIndex: 20}}>
              <small>{error}</small>
          </div>
      )}
    </div>
  );
};

export default React.memo(RouteMap);
